import { create } from 'zustand';
import { useAppointmentDetailsSlideStore } from './appointmentDetailsSlide';
import { IAppointmentsStore } from './types';
import { appointmentsApi, chatApi } from '@/API';
import { useSurveyStore } from '@/stores/survey';
import type {
  TGetAppointmentsParamsCamel,
  TCreateAppointmentRequestCamel,
  TRescheduleAppointmentRequestCamel,
  TPatchAppointmentRequestCamel,
  TAppointmentCamel,
  ICreateAppointmentData,
  TUpdateOutreachStatusRequestCamel,
  IGetAppointmentsResponse,
} from '@/types';
import { convertKeys } from '@/utils/common';
import { loadInChunks } from '@/utils/loadInChunks';

// Appointments are fetched in fixed-size pages.
const APPOINTMENTS_PER_PAGE = 150;

// Fetches one page of appointments for a given endpoint (snake-cased params
// are already baked into the closure by the caller).
type TFetchAppointmentsPage = (
  page: number,
  perPage: number,
) => Promise<IGetAppointmentsResponse>;

// Shared chunked-loading wrapper for getAppointments / getAppointmentsAll.
// Delegates the sequential pagination to loadInChunks and owns the store's
// loading flags: `loading` gates only the first page — once it lands the UI is
// unblocked and the rest stream in the background — while
// `isAllAppointmentsLoaded` stays false until every page has been appended.
const loadAppointments = async (
  set: (
    partial:
      | Partial<IAppointmentsStore>
      | ((state: IAppointmentsStore) => Partial<IAppointmentsStore>),
  ) => void,
  fetchPage: TFetchAppointmentsPage,
) => {
  set({ loading: true, isAllAppointmentsLoaded: false, appointments: [] });
  try {
    await loadInChunks(
      fetchPage,
      {
        onFirstPage: (items) =>
          // Unblock the UI as soon as the first chunk is in.
          set({ appointments: convertKeys(items), loading: false }),
        onNextChunk: (items) => {
          const chunk: TAppointmentCamel[] = convertKeys(items);
          set((state) => ({ appointments: [...state.appointments, ...chunk] }));
        },
      },
      APPOINTMENTS_PER_PAGE,
    );

    set({ isAllAppointmentsLoaded: true });
  } catch (error) {
    console.error('Failed to fetch appointments:', error);
    // Reset loading in the error path too — the first chunk may have failed.
    set({ loading: false });
    throw error;
  }
};

export const useAppointmentsStore = create<IAppointmentsStore>((set, get) => ({
  fetchFlag: false,
  triggerAppointmentFetch: () => {
    set({ fetchFlag: !get().fetchFlag });
  },
  loading: false,
  isAllAppointmentsLoaded: false,
  appointments: [],
  clearAppointmentsStore: () => {
    set({
      appointments: [],
    });
  },

  changeField: (state, field) => set(() => ({ [field]: state })),

  filterAppointmentById: (id, role) => {
    return get().appointments.filter((item) =>
      item.participants.some((participant) => {
        return (
          participant.user?.userId === id &&
          (!role || role === participant.user?.role)
        );
      }),
    );
  },

  getAppointments: async (params: TGetAppointmentsParamsCamel) => {
    const snakeParams = convertKeys(params, 'snake');
    await loadAppointments(set, (page, perPage) =>
      appointmentsApi.getAppointments({
        ...snakeParams,
        page,
        per_page: perPage,
      }),
    );
  },

  getAppointmentsByUserId: (userId) => {
    return get().appointments.filter(({ participants }) =>
      participants.some(({ user }) => user?.userId === userId),
    );
  },

  getAppointmentsByRoleIds: (roleIds) => {
    return get().appointments.filter(({ participants }) =>
      participants.some(({ userInstitutionRoleId }) =>
        roleIds.includes(userInstitutionRoleId),
      ),
    );
  },

  getAppointmentById: (id: string) => {
    return get().appointments.find((appointment) => appointment.id === id);
  },

  getAppointmentByWaitingRoomId: (waitingRoomId: string) => {
    return get().appointments.find(
      (appointment) => appointment.waitingRoom?.id === waitingRoomId,
    );
  },

  getAppointmentByChatId: (chatId) =>
    get().appointments.find((appointment) => appointment.chatId === chatId),

  getAppointmentByEncounterId: (encounterId) =>
    get().appointments.find(
      (appointment) => appointment.encounterId === encounterId,
    ),

  sendAppointmentEvent: async (id, body) => {
    return await appointmentsApi.sendAppointmentEvent(id, body);
  },

  getAppointment: async (id: string) => {
    const response = await appointmentsApi.getAppointment(id);
    return convertKeys(response.data.data);
  },

  joinAppointmentByInvite: async (appointmentId, userInstitutionRoleId) => {
    await appointmentsApi.addParticipant(
      appointmentId,
      convertKeys({ userInstitutionRoleId, isInvited: true }, 'snake'),
    );
    const response = await appointmentsApi.getAppointment(appointmentId);
    const appointment: TAppointmentCamel = convertKeys(response.data.data);
    await chatApi.putChatParticipants(
      appointment.chatId,
      convertKeys({ userInstitutionRoleId }, 'snake'),
    );
    return appointment;
  },

  getAppointmentsAll: async (params) => {
    const snakeParams = convertKeys(params, 'snake');
    await loadAppointments(set, (page, perPage) =>
      appointmentsApi.getAppointmentsAll({
        ...snakeParams,
        page,
        per_page: perPage,
      }),
    );
  },

  createAppointment: async (data: ICreateAppointmentData) => {
    set({ loading: true });
    try {
      const body: Partial<TCreateAppointmentRequestCamel> = {
        institutionId: data.institutionId,
        infermedicaSurveyId: data.infermedicaSurveyId,
        isabelSurveyId: useSurveyStore.getState().isabelTriageData?.id,
        institutionServiceId: data.institutionServiceId ?? '',
        location: data.location,
        communicationChannel: data.communicationChannel,
        scheduleType: data.scheduleType,
        start: data.start,
        duration: data.duration ?? 30,
        consented: data.consented,
        documentIds: data.documentIds,
        userInstitutionRoleIds: data.userInstitutionRoleIds,
        manualSymptoms: data.manualSymptoms ?? undefined,
        kioskId: data.kioskId,
      };
      if (data.insurance?.type === 'yes') {
        body.insurance = {
          id: data.insurance.id,
          isPremium: data.insurance.isPremium,
        };
      }
      const snakeBody = convertKeys(body, 'snake');
      const response = await appointmentsApi.createAppointment(snakeBody);
      const appointmentCamel = convertKeys(response.data.data);
      get().updateAppointment(appointmentCamel);
      return appointmentCamel;
    } catch (error) {
      console.error('Create appointment failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  rescheduleAppointment: async (
    appointmentId: string,
    data: TRescheduleAppointmentRequestCamel,
  ) => {
    set({ loading: true });
    try {
      const snakeData = convertKeys(data, 'snake');
      await appointmentsApi.rescheduleAppointment(appointmentId, snakeData);

      set((state) => {
        const updatedAppointments = state.appointments.map((appointment) => {
          if (appointment.id !== appointmentId) return appointment;

          const endTime = new Date(
            new Date(data.start).getTime() + data.duration * 60000,
          );
          const updatedAppointment = {
            ...appointment,
            start: data.start,
            end: endTime.toISOString(),
          };

          const detailsStore = useAppointmentDetailsSlideStore.getState();
          if (detailsStore.selectedAppointment?.id === appointmentId) {
            detailsStore.setSelectedAppointment(updatedAppointment);
          }

          return updatedAppointment;
        });

        return { appointments: updatedAppointments };
      });
    } catch (error) {
      console.error('Reschedule appointment failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  cancelAppointment: async (appointmentId: string, reason?: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.cancelAppointment(appointmentId, reason);
    } catch (error) {
      console.error('Cancel appointment failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  rescheduleDeclineAppointment: async (appointmentId: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.rescheduleDeclineAppointment(appointmentId);
    } catch (error) {
      console.error('Cancel appointment failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  patchAppointment: async (
    appointmentId: string,
    body: Partial<TPatchAppointmentRequestCamel>,
    suppressLoading = false,
  ) => {
    if (!suppressLoading) {
      set({ loading: true });
    }
    try {
      if (Object.keys(body).length === 0) {
        console.error('Patch appointment failed:', 'No valid data to update');
      }
      const snakeBody = convertKeys(body, 'snake');
      await appointmentsApi.patchAppointment(appointmentId, snakeBody);

      set((state) => {
        const updatedAppointments = state.appointments.map((appointment) => {
          if (appointment.id === appointmentId) {
            return {
              ...appointment,
              ...body,
            };
          }
          return appointment;
        });
        return { appointments: updatedAppointments };
      });
      const detailsStore = useAppointmentDetailsSlideStore.getState();
      const appointment = get().appointments.find(
        ({ id }) => appointmentId === id,
      );
      if (
        appointment &&
        detailsStore.selectedAppointment?.id === appointment.id
      ) {
        detailsStore.setSelectedAppointment({
          ...appointment,
          ...body,
        });
      }
    } catch (error) {
      console.error('Patch appointment failed:', error);
      throw error;
    } finally {
      if (!suppressLoading) {
        set({ loading: false });
      }
    }
  },

  addVisitNote: async (appointmentId, body) => {
    set({ loading: true });
    try {
      const response = await appointmentsApi.addVisitNote(
        appointmentId,
        convertKeys(body, 'snake'),
      );
      // The note is stamped server-side, so the response is the source of truth.
      const visitNotes = convertKeys(response.data.data).visitNotes;

      set((state) => ({
        appointments: state.appointments.map((appointment) =>
          appointment.id === appointmentId
            ? { ...appointment, visitNotes }
            : appointment,
        ),
      }));

      const detailsStore = useAppointmentDetailsSlideStore.getState();
      const selected = detailsStore.selectedAppointment;

      if (selected?.id === appointmentId) {
        detailsStore.setSelectedAppointment({ ...selected, visitNotes });
      }
    } catch (error) {
      console.error('Add visit note failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  confirmAppointment: async (appointmentId: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.confirmAppointment(appointmentId);
    } catch (error) {
      console.error('Confirm appointment failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  dispatcherAppointmentApproved: async (appointmentId: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.dispatcherAppointmentApproved(appointmentId);
    } catch (error) {
      console.error('Dispatcher approved failed:', error);
    } finally {
      set({ loading: false });
    }
  },

  dispatcherAppointmentDeclined: async (
    appointmentId: string,
    reason?: string,
  ) => {
    set({ loading: true });
    try {
      await appointmentsApi.dispatcherAppointmentDeclined(
        appointmentId,
        reason,
      );
    } catch (error) {
      console.error('Dispatcher declined failed:', error);
    } finally {
      set({ loading: false });
    }
  },

  dispatcherAppointmentPay: async (appointmentId: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.dispatcherAppointmentPay(appointmentId);
    } catch (error) {
      console.error('Dispatcher pay failed:', error);
    } finally {
      set({ loading: false });
    }
  },

  dispatcherAppointmentCopay: async (appointmentId: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.dispatcherAppointmentCopay(appointmentId);
    } catch (error) {
      console.error('Dispatcher copay failed:', error);
    } finally {
      set({ loading: false });
    }
  },

  updateOutreachStatus: async (
    appointmentId: string,
    body: TUpdateOutreachStatusRequestCamel,
  ) => {
    set({ loading: true });
    try {
      const snakeBody = convertKeys(body, 'snake');
      await appointmentsApi.updateOutreachStatus(appointmentId, snakeBody);
    } catch (error) {
      console.error('Update outreach status failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  addOutreachNote: async (appointmentId: string, text: string) => {
    set({ loading: true });
    try {
      await appointmentsApi.addOutreachNote(appointmentId, { text });
    } catch (error) {
      console.error('Add outreach note failed:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateAppointment: (appointment: TAppointmentCamel) => {
    const { appointments } = get();
    const detailsStore = useAppointmentDetailsSlideStore.getState();
    const findAppointmentIndex = appointments.findIndex(
      ({ id }) => appointment.id === id,
    );
    if (findAppointmentIndex < 0) {
      set({ appointments: [...appointments, appointment] });
      return;
    }
    const prev = appointments[findAppointmentIndex];
    const merged = { ...prev, ...appointment };
    const newAppointments = [...appointments];
    newAppointments[findAppointmentIndex] = merged;
    if (detailsStore.selectedAppointment?.id === merged.id) {
      detailsStore.setSelectedAppointment(merged);
    }
    set({ appointments: newAppointments });
  },

  getAppointmentEntriesByAppointmentId: (id: string) => {
    const { appointments } = get();
    const targetAppointment = appointments.find((item) => item.id === id);
    return targetAppointment?.formEntries ? targetAppointment.formEntries : [];
  },

  getAppointmentEntryByEntryId: (id: string) => {
    const { appointments } = get();
    const allEntries = appointments.flatMap((item) => item.formEntries);
    return allEntries.find((item) => item.id === id) ?? null;
  },

  getAllAppointmentsEntries: () => {
    const { appointments } = get();
    return appointments.flatMap((item) => item.formEntries);
  },
}));
